/**
 * Deckungsbeitrag — Deckungsbeitrags-Kalkulation und Margen-Analyse pro Kunde.
 *
 * Features:
 * - Uebersicht: DB-Marge pro Kunde mit Ampel-System
 * - Detail-Ansicht: Auftragsebene mit Materialkosten, Personalkosten, SOP-Pauschale
 * - Kategorien-Verwaltung fuer Kostenarten
 * - Neuberechnung per Background-Job, Quality-Summary
 * - MA-Effizienz-Ranking
 *
 * @page /deckungsbeitrag
 * @roles admin, manager
 * @api /api/deckungsbeitrag/uebersicht, /api/deckungsbeitrag/kunde/:id
 * @api /api/deckungsbeitrag/auftrag/:name, /api/deckungsbeitrag/kategorien
 * @api /api/deckungsbeitrag/kunde/:id/sop-pauschale
 * @api /api/jobs/deckungsbeitrag/berechnen, /api/jobs/:id/status
 * @api /api/reports/quality-summary, /api/performance/ma-effizienz/ranking
 * @i18n cm
 */
import { useEffect, useMemo, useState, useCallback, Fragment, useRef } from 'react';
import { Link } from 'react-router-dom';
import useTabParam from '../hooks/useTabParam';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { getDateRange } from '../utils/dateUtils';
import DateRangeFilter from '../components/DateRangeFilter';
import { AlertTriangle, Info, Settings, RefreshCw, ArrowLeft, Search, DollarSign, Gauge, Activity, Timer, TrendingUp } from 'lucide-react';
import { Card, KpiCard, Badge, Button, PageHeader, Modal } from '../components/ui';


const API = import.meta.env.VITE_API_URL || '';

function fmt(n, d = 0) {
  if (n == null) return '–';
  return Number(n).toLocaleString('de-DE', { minimumFractionDigits: d, maximumFractionDigits: d });
}
function fmtEur(n) {
  if (n == null) return '–';
  return Number(n).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}
/** Compact EUR formatting: 35,6K € for >=10K, 1,2M € for >=1M, full value as title */
function fmtEurCompact(n) {
  if (n == null) return { text: '–', full: null };
  const v = Number(n);
  const abs = Math.abs(v);
  const full = fmtEur(v);
  if (abs >= 1000000) return { text: (v / 1000000).toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + 'M €', full };
  if (abs >= 10000) return { text: (v / 1000).toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + 'K €', full };
  if (abs >= 1000) return { text: Math.round(v).toLocaleString('de-DE') + ' €', full };
  return { text: full, full: null };
}
function fmtDate(d) {
  if (!d) return '–';
  const s = String(d);
  // ISO datetime strings need local timezone parsing to avoid UTC shift
  const dt = s.includes('T') ? new Date(s) : new Date(s.slice(0, 10) + 'T12:00:00');
  return `${dt.getDate().toString().padStart(2, '0')}.${(dt.getMonth() + 1).toString().padStart(2, '0')}.${String(dt.getFullYear()).slice(2)}`;
}

// Get today's date as YYYY-MM-DD in Europe/Vienna timezone
function getTodayVienna() {
  return new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Vienna' });
}
// Normalize a date value (string or ISO datetime) to YYYY-MM-DD
function normDateStr(d) {
  if (!d) return '';
  const s = String(d);
  if (s.includes('T')) {
    const dt = new Date(s);
    return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
  }
  return s.slice(0, 10);
}

function margeColor(pct, z) {
  if (pct >= (z?.margin_target_good || 25)) return '#34d399';
  if (pct >= (z?.margin_target_min || 15)) return '#fbbf24';
  if (pct >= (z?.margin_target_bad || 10)) return '#f97316';
  return '#ef4444';
}
function margeBg(pct, z) {
  if (pct >= (z?.margin_target_good || 25)) return 'bg-emerald-500/15 text-emerald-400';
  if (pct >= (z?.margin_target_min || 15)) return 'bg-yellow-500/15 text-yellow-400';
  if (pct >= (z?.margin_target_bad || 10)) return 'bg-orange-500/15 text-orange-400';
  return 'bg-red-500/15 text-red-400';
}
function margeLabel(pct, z, t) {
  if (pct >= (z?.margin_target_good || 25)) return t ? t('cm.status.goal_reached') : 'Ziel erreicht';
  if (pct >= (z?.margin_target_min || 15)) return t ? t('cm.status.ok') : 'OK';
  if (pct >= (z?.margin_target_bad || 10)) return t ? t('cm.status.warning') : 'Achtung';
  return t ? t('cm.status.critical') : 'Kritisch';
}

/* ─── Info Badge / Tooltip ────────────────────────────── */
function InfoBadge({ children, warn = false }) {
  const [show, setShow] = useState(false);
  const ref = useRef(null);
  const tipRef = useRef(null);

  useEffect(() => {
    if (!show || !tipRef.current || !ref.current) return;
    const badge = ref.current.getBoundingClientRect();
    const tip = tipRef.current;
    const tipRect = tip.getBoundingClientRect();
    // Position above the badge, centered
    tip.style.left = `${badge.left + badge.width / 2 - tipRect.width / 2}px`;
    tip.style.top = `${badge.top - tipRect.height - 8}px`;
    // Keep in viewport
    const r = tip.getBoundingClientRect();
    if (r.left < 8) tip.style.left = '8px';
    if (r.right > window.innerWidth - 8) tip.style.left = `${window.innerWidth - 8 - tipRect.width}px`;
    if (r.top < 8) { tip.style.top = `${badge.bottom + 8}px`; }
  }, [show]);

  return (
    <span className="relative inline-flex items-center" ref={ref}
      onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)}>
      <span className={`cursor-help text-[10px] ${warn ? 'text-yellow-400' : 'text-[var(--text-tertiary)] hover:text-[var(--text-secondary)]'}`}>
        {warn ? <AlertTriangle className="w-3 h-3" /> : <Info className="w-3 h-3" />}
      </span>
      {show && (
        <div ref={tipRef} className="fixed z-[9999] bg-[#1e1e2e] border border-line rounded-lg shadow-2xl p-3 text-xs text-content-secondary max-w-sm whitespace-pre-line"
          style={{ pointerEvents: 'none' }}>
          {children}
        </div>
      )}
    </span>
  );
}

/* ─── Tooltip builders for each cell type ─────────────── */
function HandlingCostTip({ o }) {
  const { t } = useLanguage();
  const noTime = !o.pick_zeit_min && !o.pack_zeit_min;
  const pickRate = o.picker_stundensatz || 27;
  const packRate = o.packer_stundensatz || 27;
  const pickCost = (o.pick_zeit_min / 60) * pickRate;
  const packCost = (o.pack_zeit_min / 60) * packRate;
  const isSop = o.is_sop;
  const isBulk = o.pack_typ === 'bulk_pauschale';
  const keineDaten = o.pack_typ === 'keine_daten';
  if (noTime) return (
    <span className="text-yellow-400">{t('cm.tooltip.no_time_data')}</span>
  );
  return (
    <span>
      <strong>{t('cm.tooltip.staff_cost_calc')}</strong>
      {isSop && <span className="text-cyan-400"> ({t('cm.tooltip.sop_wave')})</span>}{'\n\n'}
      Pick: {fmt(o.pick_zeit_min, 1)}min ÷ 60 × {fmt(pickRate, 2)} €/h = {fmtEur(pickCost)}{'\n'}
      {isBulk ? (
        <>Pack: {fmt(o.pack_zeit_min, 1)}min <span className="text-cyan-400">({t('cm.tooltip.bulk_flat_rate')})</span> ÷ 60 × {fmt(packRate, 2)} €/h = {fmtEur(packCost)}{'\n'}</>
      ) : keineDaten ? (
        <>Pack: <span className="text-yellow-400">{t('cm.tooltip.no_pack_data')}</span> – 0,00 €{'\n'}</>
      ) : (
        <>Pack: {fmt(o.pack_zeit_min, 1)}min ÷ 60 × {fmt(packRate, 2)} €/h = {fmtEur(packCost)}{'\n'}</>
      )}
      {'─'.repeat(30)}{'\n'}
      {t('common.total')}: {fmtEur(o.kosten_personal)}{'\n\n'}
      {o.picker_name && <>Picker: {o.picker_name} ({fmt(pickRate, 2)} €/h){'\n'}</>}
      {o.packer_name && <>Packer: {o.packer_name} ({fmt(packRate, 2)} €/h){'\n'}</>}
      {o.wave_name && <>Wave: {o.wave_name}{'\n'}</>}
      {o.wave_typ === 'mixed' && <><span className="text-orange-400">{'\n'}&#9889; Mixed-Wave: {fmt(o.pick_anteil, 0)}% {t('cm.tooltip.share')}</span>{'\n'}</>}
      {o.uses_default_rate && <><span className="text-yellow-400">{'\n'}{t('cm.tooltip.default_rate_used')}</span></>}
      {isBulk && <><span className="text-cyan-400">{'\n'}{t('cm.tooltip.sop_bulk_info')}</span></>}
    </span>
  );
}


function TrendChart({ data, dataKey, label, color = '#34d399' }) {
  const { t } = useLanguage();
  if (!data || data.length < 2) return <div className="text-xs text-[var(--text-tertiary)] py-4 text-center">{t('cm.not_enough_data')}</div>;
  const W = 600, H = 120;
  const values = data.map(d => d[dataKey] || 0);
  const max = Math.max(...values, 1);
  const pad = { t: 10, r: 10, b: 24, l: 10 };
  const cw = W - pad.l - pad.r, ch = H - pad.t - pad.b;
  const pts = values.map((v, i) => `${pad.l + (i / (values.length - 1)) * cw},${pad.t + ch - (v / max) * ch}`).join(' ');
  const area = `${pad.l},${pad.t + ch} ${pts} ${pad.l + cw},${pad.t + ch}`;
  return (
    <div>
      <div className="text-[10px] text-[var(--text-secondary)] font-semibold uppercase tracking-wider mb-1">{label}</div>
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} className="overflow-visible">
        <polyline points={area} fill={color} fillOpacity={0.08} stroke="none" />
        <polyline points={pts} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
        {data.map((d, i) => <text key={i} x={pad.l + (i / (values.length - 1)) * cw} y={H - 4} fill="var(--text-muted)" fontSize={9} textAnchor="middle">{d.monat?.slice(5) || ''}</text>)}
        {values.map((v, i) => <circle key={i} cx={pad.l + (i / (values.length - 1)) * cw} cy={pad.t + ch - (v / max) * ch} r={2.5} fill={color} opacity={0.7} />)}
      </svg>
    </div>
  );
}

function ScatterPlot({ data, ziele }) {
  const { t } = useLanguage();
  if (!data || data.length < 2) return <div className="text-xs text-[var(--text-tertiary)] py-4 text-center">{t('cm.not_enough_data')}</div>;
  const W = 600, H = 300;
  const pad = { t: 20, r: 20, b: 30, l: 50 };
  const cw = W - pad.l - pad.r, ch = H - pad.t - pad.b;
  const maxX = Math.max(...data.map(d => d.umsatz), 1);
  const minY = Math.min(...data.map(d => d.marge_pct), -5);
  const maxY = Math.max(...data.map(d => d.marge_pct), 30);
  const rangeY = maxY - minY || 1;
  const goodLine = ziele?.margin_target_good || 25;
  const minLine = ziele?.margin_target_min || 15;
  const goodY = pad.t + ch - ((goodLine - minY) / rangeY) * ch;
  const minLineY = pad.t + ch - ((minLine - minY) / rangeY) * ch;
  return (
    <div>
      <div className="text-[10px] text-[var(--text-secondary)] font-semibold uppercase tracking-wider mb-1">{t('cm.chart.revenue_vs_margin')}</div>
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} className="overflow-visible">
        <line x1={pad.l} y1={goodY} x2={W - pad.r} y2={goodY} stroke="#34d399" strokeWidth={1} strokeDasharray="4,4" opacity={0.5} />
        <line x1={pad.l} y1={minLineY} x2={W - pad.r} y2={minLineY} stroke="#fbbf24" strokeWidth={1} strokeDasharray="4,4" opacity={0.5} />
        <text x={pad.l - 4} y={goodY + 3} fill="#34d399" fontSize={8} textAnchor="end">{goodLine}%</text>
        <text x={pad.l - 4} y={minLineY + 3} fill="#fbbf24" fontSize={8} textAnchor="end">{minLine}%</text>
        <line x1={pad.l} y1={pad.t} x2={pad.l} y2={H - pad.b} stroke="var(--line)" strokeWidth={1} />
        <line x1={pad.l} y1={H - pad.b} x2={W - pad.r} y2={H - pad.b} stroke="var(--line)" strokeWidth={1} />
        {data.map((d, i) => {
          const x = pad.l + (d.umsatz / maxX) * cw;
          const y = pad.t + ch - ((d.marge_pct - minY) / rangeY) * ch;
          return (
            <g key={i}>
              <circle cx={x} cy={y} r={Math.min(8, Math.max(3, Math.sqrt(d.auftraege) * 0.5))} fill={margeColor(d.marge_pct, ziele)} opacity={0.7} />
              {data.length <= 15 && <text x={x} y={y - 10} fill="var(--text-muted)" fontSize={8} textAnchor="middle">{d.name?.slice(0, 12)}</text>}
            </g>
          );
        })}
        <text x={W / 2} y={H - 4} fill="var(--text-muted)" fontSize={9} textAnchor="middle">{t('cm.revenue')}</text>
      </svg>
    </div>
  );
}

/* ─── Order Detail (expanded row) ─────────────────────── */
function OrderDetail({ pickingName, sollStammzeit, sollTop25 }) {
  const { t } = useLanguage();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/api/deckungsbeitrag/auftrag/${encodeURIComponent(pickingName)}`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => { setDetail(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [pickingName]);

  if (loading) return <div className="p-4 text-center text-[var(--text-tertiary)] text-xs">{t('cm.loading_details')}</div>;
  if (!detail || detail.error) return <div className="p-4 text-center text-[var(--text-tertiary)] text-xs">{t('cm.no_details')}</div>;

  const lines = detail.billing_lines || [];
  const handling = lines.filter(l => l.kategorie === 'handling');
  const verpackung = lines.filter(l => l.kategorie === 'verpackung');
  const versand = lines.filter(l => l.kategorie === 'versand');

  const sumHandlingU = handling.reduce((s, l) => s + (l.umsatz || 0), 0);
  const sumVerpackungU = verpackung.reduce((s, l) => s + (l.umsatz || 0), 0);
  const sumVerpackungK = verpackung.reduce((s, l) => s + (l.kosten || 0), 0);
  const sumVersandU = versand.reduce((s, l) => s + (l.umsatz || 0), 0);
  const sumVersandK = versand.reduce((s, l) => s + (l.kosten || 0), 0);

  function LineRow({ l }) {
    return (
      <tr className="border-b border-line/50">
        <td className="py-1.5 pl-6 text-xs text-content-secondary truncate max-w-[300px]" title={l.name}>
          {l.name}{l.quantity > 1 ? ` (${l.quantity}x)` : ''}
        </td>
        <td className="py-1.5 text-right font-mono text-xs text-content-secondary">{fmtEur(l.umsatz)}</td>
        <td className="py-1.5 text-right font-mono text-xs text-content-secondary">{l.kosten > 0 ? fmtEur(l.kosten) : '–'}</td>
        <td className="py-1.5 text-right font-mono text-xs text-[var(--text-primary)]">
          {fmtEur(l.umsatz - l.kosten)}
        </td>
      </tr>
    );
  }

  function SectionSum({ label, umsatz, kosten, color }) {
    const db = umsatz - kosten;
    return (
      <tr className="border-b border-line bg-surface-inset/30">
        <td className="py-1.5 pl-4 text-xs font-bold text-content-tertiary">
          <span className="inline-block w-2 h-2 rounded-full mr-1.5" style={{ backgroundColor: color }} />
          {t('cm.sum')} {label}
        </td>
        <td className="py-1.5 text-right font-mono text-xs font-bold text-content-secondary">{fmtEur(umsatz)}</td>
        <td className="py-1.5 text-right font-mono text-xs font-bold text-content-secondary">{kosten > 0 ? fmtEur(kosten) : '–'}</td>
        <td className="py-1.5 text-right font-mono text-xs font-bold" style={{ color: 'var(--text-primary)' }}>{fmtEur(db)}</td>
      </tr>
    );
  }

  return (
    <div className="bg-surface-inset/50 border-t border-line px-4 py-3">
      <div className="text-xs font-bold text-content mb-2">{detail.picking_name} – {t('cm.detail')}</div>
      <table className="w-full text-sm mb-3">
        <thead>
          <tr className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wider border-b border-line">
            <th className="py-1 pl-4 text-left">{t('cm.position')}</th>
            <th className="py-1 text-right">{t('cm.revenue')}</th>
            <th className="py-1 text-right">{t('cm.costs')}</th>
            <th className="py-1 text-right">{t('cm.cm_short')}</th>
          </tr>
        </thead>
        <tbody>
          {handling.map((l, i) => <LineRow key={`h${i}`} l={l} />)}
          {handling.length > 0 && <SectionSum label={t('cm.handling')} umsatz={sumHandlingU} kosten={detail.kosten_personal} color="#338edc" />}
          {verpackung.map((l, i) => <LineRow key={`v${i}`} l={l} />)}
          {verpackung.length > 0 && <SectionSum label={t('cm.packaging')} umsatz={sumVerpackungU} kosten={sumVerpackungK} color="#4D9CE0" />}
          {versand.map((l, i) => <LineRow key={`s${i}`} l={l} />)}
          {versand.length > 0 && <SectionSum label={t('cm.shipping')} umsatz={sumVersandU} kosten={sumVersandK} color="#06b6d4" />}
          {lines.length === 0 && (
            <tr><td colSpan={4} className="py-2 text-center text-xs text-[var(--text-tertiary)]">{t('cm.no_billing_lines')}</td></tr>
          )}
        </tbody>
        <tfoot className="border-t-2 border-line-hover">
          <tr>
            <td className="py-2 pl-4 text-xs font-extrabold text-content">{t('cm.grand_total')}</td>
            <td className="py-2 text-right font-mono text-xs font-extrabold text-content">{fmtEur(detail.umsatz_gesamt)}</td>
            <td className="py-2 text-right font-mono text-xs font-extrabold text-content">{fmtEur(detail.kosten_gesamt)}</td>
            <td className="py-2 text-right font-mono text-xs font-extrabold text-content">
              {fmtEur(detail.db_gesamt)}
              <span className="ml-1 text-[10px] opacity-70">{fmt(detail.db_prozent, 1)}%</span>
            </td>
          </tr>
        </tfoot>
      </table>
      <div className="flex flex-wrap items-center gap-3 text-[10px] text-[var(--text-tertiary)]">
        {detail.wave_name && <span className="text-[var(--text-secondary)]">Wave: <span className="font-mono">{detail.wave_name}</span></span>}
        {detail.is_sop && <Badge variant="info" className="text-[9px]">SOP</Badge>}
        {detail.pack_typ === 'bulk_pauschale' && <Badge variant="warning" className="text-[9px]">Bulk-Pack</Badge>}
        {detail.pack_typ === 'keine_daten' && <Badge variant="error" className="text-[9px]">{t('cm.tooltip.no_pack_data')}</Badge>}
        {detail.picker_name && <span>Pick: {fmt(detail.pick_zeit_min, 1)}min ({detail.picker_name}, {fmt(detail.picker_stundensatz || 27, 2)} €/h)</span>}
        {detail.packer_name && <span>Pack: {fmt(detail.pack_zeit_min, 1)}min ({detail.packer_name}, {fmt(detail.packer_stundensatz || 27, 2)} €/h)</span>}
        {!detail.packer_name && detail.pack_typ === 'bulk_pauschale' && <span>Pack: {fmt(detail.pack_zeit_min, 1)}min ({t('cm.flat_rate')})</span>}
        {detail.carrier && <span>Carrier: {detail.carrier}</span>}
        {detail.uses_default_rate && <span className="text-yellow-400 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {t('cm.default_rate')}</span>}
      </div>

      {/* Personalkosten-Vergleich (SOLL/IST) */}
      {(sollStammzeit || sollTop25) && (
        <div className="mt-3 border-t border-line pt-3">
          <div className="text-[10px] font-semibold text-[var(--text-tertiary)] uppercase tracking-wider mb-2">{t('cm.personalVergleich')}</div>
          <div className="space-y-1.5">
            {/* IST row */}
            <div className="flex items-center gap-3 text-xs">
              <span className="w-24 text-[var(--text-secondary)] font-medium">IST</span>
              <span className="w-20 text-right font-mono text-[var(--text-primary)]">{fmtEur(detail.kosten_personal)}</span>
              <span className="text-[10px] text-[var(--text-tertiary)]">
                ({t('cm.pickKosten')} {fmtEur((detail.pick_zeit_min / 60) * (detail.picker_stundensatz || 27))} + {t('cm.packKosten')} {fmtEur((detail.pack_zeit_min / 60) * (detail.packer_stundensatz || 27))})
              </span>
              <span className="ml-auto font-mono text-xs text-[var(--text-primary)]">{fmt(detail.db_prozent, 1)}%</span>
            </div>
            {/* SOLL Stammzeit row */}
            {sollStammzeit && (
              <div className="flex items-center gap-3 text-xs">
                <span className="w-24 text-[var(--text-secondary)]">{t('cm.sollStammzeit')}</span>
                <span className="w-20 text-right font-mono text-[var(--text-secondary)]">{fmtEur(sollStammzeit.gesamt)}</span>
                <span className="text-[10px] text-[var(--text-tertiary)]">
                  ({t('cm.pickKosten')} {fmtEur(sollStammzeit.pick_kosten)} + {t('cm.packKosten')} {fmtEur(sollStammzeit.pack_kosten)})
                </span>
                <span className="ml-auto font-mono text-xs text-[var(--text-secondary)]">{fmt(sollStammzeit.db_pct, 1)}%</span>
              </div>
            )}
            {/* SOLL Top25 row */}
            {sollTop25 && (
              <div className="flex items-center gap-3 text-xs">
                <span className="w-24 text-[var(--text-secondary)]">{t('cm.sollTop25')}</span>
                <span className="w-20 text-right font-mono text-[var(--text-secondary)]">{fmtEur(sollTop25.gesamt)}</span>
                <span className="text-[10px] text-[var(--text-tertiary)]">
                  ({t('cm.pickKosten')} {fmtEur(sollTop25.pick_kosten)} + {t('cm.packKosten')} {fmtEur(sollTop25.pack_kosten)})
                </span>
                <span className="ml-auto font-mono text-xs text-[var(--text-secondary)]">{fmt(sollTop25.db_pct, 1)}%</span>
              </div>
            )}
            {/* Delta line */}
            {(sollTop25?.delta != null || sollStammzeit?.delta != null) && (() => {
              const delta = sollTop25?.delta ?? sollStammzeit?.delta;
              const isPositive = delta > 0;
              return (
                <div className="flex items-center gap-3 text-xs pt-1 border-t border-line/50">
                  <span className="w-24 text-[var(--text-secondary)]">{t('cm.delta')}</span>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold ${
                    isPositive ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'
                  }`}>
                    {isPositive ? t('cm.einsparung') : t('cm.mehrkosten')}: {isPositive ? '+' : ''}{fmtEur(delta)}
                  </span>
                </div>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Kategorien-Mapping Modal ────────────────────────── */
function KategorienModal({ open, onClose, onChanged }) {
  const { t } = useLanguage();
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [addMode, setAddMode] = useState(false);
  const [newRule, setNewRule] = useState({ produkt_name_pattern: '', kategorie: 'handling', match_typ: 'contains', prioritaet: 10 });

  const loadRules = useCallback(() => {
    setLoading(true);
    fetch(`${API}/api/deckungsbeitrag/kategorien`, { credentials: 'include' })
      .then(r => r.json()).then(d => { setRules(d.rules || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => { if (open) loadRules(); }, [open, loadRules]);

  const runTest = useCallback(() => {
    setTesting(true);
    fetch(`${API}/api/deckungsbeitrag/kategorien/test`, { credentials: 'include' })
      .then(r => r.json()).then(d => { setTestResult(d); setTesting(false); })
      .catch(() => setTesting(false));
  }, []);

  const saveRule = async (id, data) => {
    await fetch(`${API}/api/deckungsbeitrag/kategorien/${id}`, {
      method: 'PUT', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    setEditingId(null);
    loadRules();
    onChanged?.();
  };

  const addRule = async () => {
    await fetch(`${API}/api/deckungsbeitrag/kategorien`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newRule),
    });
    setAddMode(false);
    setNewRule({ produkt_name_pattern: '', kategorie: 'handling', match_typ: 'contains', prioritaet: 10 });
    loadRules();
    onChanged?.();
  };

  const deleteRule = async (id) => {
    await fetch(`${API}/api/deckungsbeitrag/kategorien/${id}`, { method: 'DELETE', credentials: 'include' });
    loadRules();
    onChanged?.();
  };

  const katColors = { handling: 'text-primary-400 bg-primary-500/15', verpackung: 'text-purple-400 bg-purple-500/15', versand: 'text-cyan-400 bg-cyan-500/15', ignorieren: 'text-[var(--text-tertiary,#6B7280)] bg-gray-500/15' };

  return (
    <Modal open={open} onClose={onClose} title={t('cm.categories.title')} size="lg"
      footer={<Button variant="secondary" size="sm" onClick={onClose}>{t('common.close')}</Button>}>
          {loading ? (
            <div className="text-center py-8 text-[var(--text-tertiary)]">{t('common.loading')}</div>
          ) : (
            <>
              <table className="w-full text-sm mb-4">
                <thead>
                  <tr className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wider border-b border-line">
                    <th className="py-2 text-left">{t('cm.categories.pattern')}</th>
                    <th className="py-2 text-left">{t('cm.categories.category')}</th>
                    <th className="py-2 text-left">{t('cm.categories.match_type')}</th>
                    <th className="py-2 text-right">{t('cm.categories.priority')}</th>
                    <th className="py-2 text-center">{t('common.active')}</th>
                    <th className="py-2 w-16"></th>
                  </tr>
                </thead>
                <tbody>
                  {rules.map(r => {
                    const isEditing = editingId === r.id;
                    return (
                      <tr key={r.id} className="border-b border-line/50 hover:bg-surface-hover transition-colors">
                        {isEditing ? (
                          <>
                            <td className="py-1.5"><input className="bg-surface-inset border border-line rounded px-2 py-1 text-xs w-full text-content" value={editForm.produkt_name_pattern} onChange={e => setEditForm({ ...editForm, produkt_name_pattern: e.target.value })} /></td>
                            <td className="py-1.5"><select className="bg-surface-inset border border-line rounded px-1 py-1 text-xs text-content" value={editForm.kategorie} onChange={e => setEditForm({ ...editForm, kategorie: e.target.value })}>
                              <option value="handling">handling</option><option value="verpackung">verpackung</option><option value="versand">versand</option><option value="ignorieren">ignorieren</option>
                            </select></td>
                            <td className="py-1.5"><select className="bg-surface-inset border border-line rounded px-1 py-1 text-xs text-content" value={editForm.match_typ} onChange={e => setEditForm({ ...editForm, match_typ: e.target.value })}>
                              <option value="contains">contains</option><option value="exact">exact</option><option value="startswith">startswith</option><option value="regex">regex</option>
                            </select></td>
                            <td className="py-1.5 text-right"><input type="number" className="bg-surface-inset border border-line rounded px-1 py-1 text-xs w-12 text-right text-content" value={editForm.prioritaet} onChange={e => setEditForm({ ...editForm, prioritaet: Number(e.target.value) })} /></td>
                            <td className="py-1.5 text-center"><input type="checkbox" checked={editForm.aktiv} onChange={e => setEditForm({ ...editForm, aktiv: e.target.checked })} /></td>
                            <td className="py-1.5 text-right">
                              <button onClick={() => saveRule(r.id, editForm)} className="text-green-400 text-xs mr-1">&#10003;</button>
                              <button onClick={() => setEditingId(null)} className="text-red-400 text-xs">&#10005;</button>
                            </td>
                          </>
                        ) : (
                          <>
                            <td className="py-1.5 text-xs font-mono text-content">{r.produkt_name_pattern}</td>
                            <td className="py-1.5"><span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${katColors[r.kategorie] || ''}`}>{r.kategorie}</span></td>
                            <td className="py-1.5 text-xs text-[var(--text-secondary)]">{r.match_typ}</td>
                            <td className="py-1.5 text-right text-xs font-mono text-[var(--text-secondary)]">{r.prioritaet}</td>
                            <td className="py-1.5 text-center">{r.aktiv ? <span className="text-green-400">&#10003;</span> : <span className="text-red-400">&#10005;</span>}</td>
                            <td className="py-1.5 text-right">
                              <button onClick={() => { setEditingId(r.id); setEditForm({ produkt_name_pattern: r.produkt_name_pattern, kategorie: r.kategorie, match_typ: r.match_typ, prioritaet: r.prioritaet, aktiv: r.aktiv }); }} className="text-primary-400 text-xs mr-1" title={t('common.edit')}>&#9998;</button>
                              <button onClick={() => deleteRule(r.id)} className="text-red-400 text-xs" title={t('common.delete')}>&#128465;</button>
                            </td>
                          </>
                        )}
                      </tr>
                    );
                  })}
                  {addMode && (
                    <tr className="border-b border-line bg-surface-hover/50">
                      <td className="py-1.5"><input className="bg-surface-inset border border-line rounded px-2 py-1 text-xs w-full text-content" placeholder={`${t('cm.categories.pattern')}...`} value={newRule.produkt_name_pattern} onChange={e => setNewRule({ ...newRule, produkt_name_pattern: e.target.value })} autoFocus /></td>
                      <td className="py-1.5"><select className="bg-surface-inset border border-line rounded px-1 py-1 text-xs text-content" value={newRule.kategorie} onChange={e => setNewRule({ ...newRule, kategorie: e.target.value })}>
                        <option value="handling">handling</option><option value="verpackung">verpackung</option><option value="versand">versand</option><option value="ignorieren">ignorieren</option>
                      </select></td>
                      <td className="py-1.5"><select className="bg-surface-inset border border-line rounded px-1 py-1 text-xs text-content" value={newRule.match_typ} onChange={e => setNewRule({ ...newRule, match_typ: e.target.value })}>
                        <option value="contains">contains</option><option value="exact">exact</option><option value="startswith">startswith</option><option value="regex">regex</option>
                      </select></td>
                      <td className="py-1.5 text-right"><input type="number" className="bg-surface-inset border border-line rounded px-1 py-1 text-xs w-12 text-right text-content" value={newRule.prioritaet} onChange={e => setNewRule({ ...newRule, prioritaet: Number(e.target.value) })} /></td>
                      <td className="py-1.5 text-center">&#10003;</td>
                      <td className="py-1.5 text-right">
                        <button onClick={addRule} disabled={!newRule.produkt_name_pattern} className="text-green-400 text-xs mr-1 disabled:opacity-30">&#10003;</button>
                        <button onClick={() => setAddMode(false)} className="text-red-400 text-xs">&#10005;</button>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              <div className="flex items-center gap-2 mb-4">
                {!addMode && <Button variant="ghost" size="xs" onClick={() => setAddMode(true)} className="text-emerald-400">+ {t('cm.categories.new_rule')}</Button>}
                <Button variant="secondary" size="xs" onClick={runTest} loading={testing}>{testing ? t('cm.categories.testing') : t('cm.categories.test')}</Button>
              </div>
              {testResult && (
                <div className="bg-surface-inset rounded-lg p-3 text-xs">
                  <div className="font-bold text-content mb-2">{t('cm.categories.test_result')}</div>
                  <div className="grid grid-cols-3 gap-2 mb-2">
                    <div>{t('common.total')}: <span className="font-mono font-bold">{testResult.total_lines}</span></div>
                    <div className="text-green-400">{t('cm.categories.matched')}: <span className="font-mono font-bold">{testResult.matched_count}</span></div>
                    <div className="text-yellow-400">{t('cm.categories.unmatched')}: <span className="font-mono font-bold">{testResult.unmatched_count}</span></div>
                  </div>
                  {testResult.unmatched?.length > 0 && (
                    <div className="mt-2 max-h-40 overflow-y-auto">
                      <div className="text-[var(--text-secondary)] mb-1">{t('cm.categories.unmatched_lines')}:</div>
                      {testResult.unmatched.map((u, i) => (
                        <div key={i} className="flex justify-between py-0.5 border-b border-line/30">
                          <span className="text-content-secondary truncate max-w-[400px]" title={u.name}>{u.product || u.name}</span>
                          <span className="font-mono text-[var(--text-secondary)]">{u.count}x</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {testResult.unmatched_count === 0 && <div className="text-green-400">{t('cm.categories.all_matched')}</div>}
                </div>
              )}
            </>
          )}
    </Modal>
  );
}

/* ─── Kunden-Detail View ──────────────────────────────── */
function KundenDetailView({ kundeId, kundeName, von, bis, ziele, onBack, isAdmin }) {
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [orderSort, setOrderSort] = useState('datum');
  const [orderSortDir, setOrderSortDir] = useState('desc');
  const [orderSearch, setOrderSearch] = useState('');
  const [sopFilter, setSopFilter] = useState('alle'); // 'alle' | 'sop' | 'normal'
  const [editPauschale, setEditPauschale] = useState(false);
  const [pauschaleVal, setPauschaleVal] = useState('');
  const [savingPauschale, setSavingPauschale] = useState(false);

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/api/deckungsbeitrag/kunde/${kundeId}?von=${von}&bis=${bis}`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [kundeId, von, bis]);

  const toggleOrderSort = (col) => {
    if (orderSort === col) setOrderSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setOrderSort(col); setOrderSortDir('desc'); }
  };

  const sortedOrders = useMemo(() => {
    if (!data?.auftraege) return [];
    const todayStr = getTodayVienna();
    let arr = data.auftraege.map(o => ({
      ...o,
      _billingPending: normDateStr(o.datum) === todayStr && o.umsatz_gesamt === 0,
    }));
    if (orderSearch) {
      const q = orderSearch.toLowerCase();
      arr = arr.filter(o => o.picking_name?.toLowerCase().includes(q) || o.carrier?.toLowerCase().includes(q) || o.picker_name?.toLowerCase().includes(q) || o.wave_name?.toLowerCase().includes(q));
    }
    if (sopFilter === 'sop') arr = arr.filter(o => o.is_sop);
    else if (sopFilter === 'normal') arr = arr.filter(o => !o.is_sop);
    arr.sort((a, b) => {
      let va = a[orderSort] ?? '', vb = b[orderSort] ?? '';
      if (orderSort === 'datum') { va = a.datum || ''; vb = b.datum || ''; }
      if (typeof va === 'string') return orderSortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
      return orderSortDir === 'asc' ? va - vb : vb - va;
    });
    return arr;
  }, [data, orderSort, orderSortDir, orderSearch, sopFilter]);

  // Count billing pending orders
  const pendingCount = useMemo(() => {
    if (!data?.auftraege) return 0;
    const todayStr = getTodayVienna();
    return data.auftraege.filter(o => normDateStr(o.datum) === todayStr && o.umsatz_gesamt === 0).length;
  }, [data]);
  const allPending = pendingCount > 0 && pendingCount === (data?.auftraege?.length || 0);

  const OrdSortHeader = ({ col, children, align = 'right' }) => (
    <th className={`py-2 px-2 cursor-pointer select-none hover:text-content-secondary transition-colors text-${align}`}
      onClick={() => toggleOrderSort(col)}>
      <span className="inline-flex items-center gap-0.5">
        {children}
        {orderSort === col && (
          <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path d={orderSortDir === 'asc' ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'} />
          </svg>
        )}
      </span>
    </th>
  );

  if (loading) return <div className="flex items-center justify-center py-20"><div className="animate-spin h-6 w-6 border-2 border-primary-500 border-t-transparent rounded-full" /></div>;
  if (!data) return <div className="text-center py-20 text-[var(--text-tertiary)]">{t('common.no_data')}</div>;

  const s = data.summary || {};

  return (
    <div>
      {/* Back + Header */}
      <div className="flex items-center gap-3 mb-section">
        <Button variant="ghost" size="sm" icon={ArrowLeft} onClick={onBack} />
        <div>
          <h2 className="text-xl font-semibold text-content">{data.kunde?.name || kundeName}</h2>
          {data.kunde?.segment && (
            <span className="inline-flex items-center gap-1.5 text-xs text-[var(--text-secondary)]">
              {t('common.segment')} <Badge variant={`segment-${data.kunde.segment.toLowerCase()}`}>{data.kunde.segment}</Badge>
            </span>
          )}
        </div>
      </div>

      {/* Billing Pending Banner */}
      {allPending && (
        <div className="mb-4 p-3 bg-gray-500/10 border border-gray-500/20 rounded-card text-sm text-[var(--text-tertiary,#6B7280)] flex items-center gap-2">
          <Badge variant="warning">⏳</Badge> {t('cm.billing_not_available')}
        </div>
      )}
      {!allPending && pendingCount > 0 && (
        <div className="mb-4 p-3 bg-gray-500/10 border border-gray-500/20 rounded-card text-sm text-[var(--text-tertiary,#6B7280)] flex items-center gap-2">
          <Badge variant="warning">⏳</Badge> {pendingCount} {pendingCount === 1 ? t('cm.order_waiting') : t('cm.orders_waiting')}
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3 mb-section">
        <KpiCard label={t('cm.orders')} value={fmt(s.auftraege)}
          trend={pendingCount > 0 ? `${pendingCount} pending` : undefined} />
        <KpiCard label={t('cm.revenue')} value={allPending ? '⏳ pending' : fmtEur(s.umsatz)}
          trend={!allPending && pendingCount > 0 ? `${pendingCount} ${t('cm.without_billing')}` : undefined} />
        <KpiCard label={t('cm.costs')} value={fmtEur(s.kosten)} />
        <KpiCard label={t('cm.cm_total')} value={allPending ? '⏳ pending' : fmtEur(s.db)}
          trend={!allPending && pendingCount > 0 ? `${pendingCount} ${t('cm.without_billing')}` : undefined} />
        <KpiCard label={t('cm.cm_margin')} value={allPending ? '⏳' : `${fmt(s.marge_pct, 1)}%`} />
        <KpiCard label={t('cm.avg_cm_per_order')} value={allPending ? '⏳' : fmtEur(s.avg_db_pro_auftrag)} />
      </div>

      {/* SOLL/IST Comparison Bar */}
      {(s.soll_stammzeit || s.soll_top25) && (
        <div className="mb-section p-3 bg-surface-card border border-line rounded-card">
          <div className="grid grid-cols-4 gap-3 text-xs">
            <div>
              <span className="text-[var(--text-secondary)]">{t('cm.cm_pct')} IST:</span>{' '}
              <b className="text-[var(--text-primary)]">{fmt(s.marge_pct, 1)}%</b>
            </div>
            <div>
              <span className="text-[var(--text-secondary)]">{t('cm.dbSollStamm')}:</span>{' '}
              <b className="text-[var(--text-primary)]">{s.soll_stammzeit ? `${fmt(s.soll_stammzeit.db_pct, 1)}%` : '–'}</b>
            </div>
            <div>
              <span className="text-[var(--text-secondary)]">{t('cm.dbSollTop25')}:</span>{' '}
              <b className="text-[var(--text-primary)]">{s.soll_top25 ? `${fmt(s.soll_top25.db_pct, 1)}%` : '–'}</b>
            </div>
            <div>
              <span className="text-[var(--text-secondary)]">{t('cm.einsparpotential')}:</span>{' '}
              {s.soll_top25?.delta != null ? (() => {
                const c = fmtEurCompact(s.soll_top25.delta);
                return (
                  <b className={s.soll_top25.delta > 0 ? 'text-primary-400' : s.soll_top25.delta < 0 ? 'text-red-400' : 'text-[var(--text-primary)]'}
                    title={c.full}>
                    {s.soll_top25.delta > 0 ? '+' : ''}{c.text}
                  </b>
                );
              })() : <b className="text-[var(--text-primary)]">–</b>}
            </div>
          </div>
        </div>
      )}

      {/* Mixed-Wave Info Bar */}
      {s.mixed_auftraege > 0 && (
        <div className="mb-section p-3 bg-orange-500/5 border border-orange-500/15 rounded-card flex flex-wrap items-center gap-3 text-xs">
          <div className="flex items-center gap-1.5">
            <Badge variant="warning" className="text-[10px]">&#9889; Mixed</Badge>
            <span className="text-content-secondary">{fmt(s.mixed_auftraege)} {t('cm.of')} {fmt(s.auftraege)} {t('cm.orders')} ({fmt(s.mixed_pct, 1)}%)</span>
          </div>
          {s.avg_pick_anteil != null && (
            <div className="flex items-center gap-1.5">
              <span className="text-[var(--text-secondary)]">&Oslash; {t('cm.tooltip.share')}:</span>
              <span className="text-content-secondary font-mono">{fmt(s.avg_pick_anteil, 0)}%</span>
            </div>
          )}
        </div>
      )}

      {/* SOP Info Bar */}
      {s.sop_auftraege > 0 && (
        <div className="mb-section p-3 bg-cyan-500/5 border border-cyan-500/15 rounded-card flex flex-wrap items-center gap-3 text-xs">
          <div className="flex items-center gap-1.5">
            <Badge variant="info" className="text-[10px]">SOP</Badge>
            <span className="text-content-secondary">{fmt(s.sop_auftraege)} {t('cm.of')} {fmt(s.auftraege)} {t('cm.orders')} ({fmt(s.sop_pct, 1)}%)</span>
          </div>
          {s.bulk_pauschale_auftraege > 0 && (
            <div className="flex items-center gap-1.5">
              <Badge variant="warning" className="text-[10px]">Bulk-Pack</Badge>
              <span className="text-content-secondary">{fmt(s.bulk_pauschale_auftraege)} {t('cm.with_flat_rate')}</span>
            </div>
          )}
          {s.keine_daten_auftraege > 0 && (
            <div className="flex items-center gap-1.5">
              <AlertTriangle className="w-3 h-3 text-yellow-400" />
              <span className="text-content-secondary">{fmt(s.keine_daten_auftraege)} {t('cm.without_pack_data')}</span>
            </div>
          )}
          <div className="ml-auto flex items-center gap-2">
            <span className="text-[var(--text-tertiary)]">{t('cm.bulk_pack_flat_rate')}:</span>
            {editPauschale ? (
              <span className="flex items-center gap-1">
                <input type="number" step="0.05" min="0" max="99" value={pauschaleVal}
                  onChange={e => setPauschaleVal(e.target.value)}
                  className="w-16 bg-surface-inset border border-line rounded px-1.5 py-0.5 text-xs text-content font-mono text-right focus:outline-none focus:border-cyan-500/40" />
                <span className="text-[var(--text-tertiary)]">min</span>
                <button disabled={savingPauschale} onClick={async () => {
                  setSavingPauschale(true);
                  try {
                    await fetch(`${API}/api/deckungsbeitrag/kunde/${kundeId}/sop-pauschale`, {
                      method: 'PUT', credentials: 'include',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ pauschale: parseFloat(pauschaleVal) || 0.75 }),
                    });
                    setEditPauschale(false);
                    // Reload data
                    const r = await fetch(`${API}/api/deckungsbeitrag/kunde/${kundeId}?von=${von}&bis=${bis}`, { credentials: 'include' });
                    if (r.ok) setData(await r.json());
                  } finally { setSavingPauschale(false); }
                }} className="text-green-400 hover:text-green-300 disabled:opacity-50">&#10003;</button>
                <button onClick={() => setEditPauschale(false)} className="text-red-400 hover:text-red-300">&#10005;</button>
              </span>
            ) : (
              <span className="flex items-center gap-1">
                <span className="font-mono font-bold text-[var(--text-primary)]">{fmt(data.kunde?.sop_bulk_pack_pauschale || 0.75, 2)}min</span>
                {isAdmin && (
                  <button onClick={() => { setPauschaleVal(String(data.kunde?.sop_bulk_pack_pauschale || 0.75)); setEditPauschale(true); }}
                    className="text-[var(--text-tertiary)] hover:text-[var(--text-secondary)] ml-1" title={t('cm.edit_flat_rate')}>&#9998;</button>
                )}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Breakdown + Trend */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-section">
        <div className="bg-surface-card border border-line rounded-card overflow-hidden">
          <div className="px-4 py-2.5 border-b border-line">
            <span className="text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wider">{t('cm.breakdown')}</span>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wider border-b border-line">
                <th className="px-4 py-2 text-left">{t('cm.categories.category')}</th>
                <th className="px-3 py-2 text-right">{t('cm.revenue')}</th>
                <th className="px-3 py-2 text-right">{t('cm.costs')}</th>
                <th className="px-3 py-2 text-right">{t('cm.cm_short')}</th>
              </tr>
            </thead>
            <tbody>
              {[
                { label: t('cm.handling_staff'), key: 'handling', color: '#338edc' },
                { label: t('cm.packaging_material'), key: 'verpackung', color: '#4D9CE0' },
                { label: t('cm.shipping_carrier'), key: 'versand', color: '#06b6d4' },
              ].map(cat => {
                const b = s.breakdown?.[cat.key] || {};
                const db = (b.umsatz || 0) - (b.kosten || 0);
                return (
                  <tr key={cat.key} className="border-b border-line hover:bg-surface-hover transition-colors">
                    <td className="px-4 py-2.5 text-xs">
                      <span className="inline-block w-2 h-2 rounded-full mr-1.5" style={{ backgroundColor: cat.color }} />{cat.label}
                    </td>
                    <td className="px-3 py-2.5 font-mono text-xs text-right text-content-secondary">{fmtEur(b.umsatz || 0)}</td>
                    <td className="px-3 py-2.5 font-mono text-xs text-right text-content-secondary">{fmtEur(b.kosten || 0)}</td>
                    <td className="px-3 py-2.5 font-mono text-xs text-right font-bold" style={{ color: 'var(--text-primary)' }}>{fmtEur(db)}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="border-t border-line-hover bg-surface-card">
              <tr>
                <td className="px-4 py-2.5 text-xs font-bold text-content-tertiary">{t('common.total')}</td>
                <td className="px-3 py-2.5 font-mono text-xs text-right font-bold text-content">{fmtEur(s.umsatz)}</td>
                <td className="px-3 py-2.5 font-mono text-xs text-right font-bold text-content">{fmtEur(s.kosten)}</td>
                <td className="px-3 py-2.5 font-mono text-xs text-right font-bold text-content">{fmtEur(s.db)}</td>
              </tr>
            </tfoot>
          </table>
          {pendingCount > 0 && (
            <div className="px-4 py-2 text-[10px] text-[var(--text-tertiary,#6B7280)] border-t border-line">
              ⏳ {pendingCount} {pendingCount === 1 ? t('cm.order') : t('cm.orders')} {t('cm.without_billing_today')}
            </div>
          )}
        </div>

        {data.trend?.length >= 2 && (
          <Card>
            <TrendChart data={data.trend} dataKey="db" label={t('cm.trend_cm_monthly')} color="#34d399" />
            <div className="mt-3">
              <TrendChart data={data.trend} dataKey="marge_pct" label={t('cm.trend_cm_pct_monthly')} color="#338edc" />
            </div>
          </Card>
        )}
      </div>

      {/* Orders table */}
      <div className="bg-surface-card border border-line rounded-card">
        <div className="px-4 py-3 border-b border-line flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-content">{t('cm.orders')}</span>
            <Badge variant="default" className="font-mono">{sortedOrders.length}</Badge>
          </div>
          <div className="flex items-center gap-2">
            {s.sop_auftraege > 0 && (
              <div className="flex bg-surface-inset rounded-lg p-0.5 border border-line-hover">
                {[
                  { key: 'alle', label: t('common.all') },
                  { key: 'sop', label: 'SOP' },
                  { key: 'normal', label: 'Normal' },
                ].map(f => (
                  <button key={f.key} onClick={() => setSopFilter(f.key)}
                    className={`px-2.5 py-1 rounded text-[10px] font-medium transition-colors ${
                      sopFilter === f.key ? 'bg-surface-active text-content' : 'text-[var(--text-secondary)] hover:text-content-secondary'
                    }`}>{f.label}</button>
                ))}
              </div>
            )}
            {data.auftraege?.length > 10 && (
              <div className="relative w-48">
                <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[var(--text-secondary)]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" /></svg>
                <input type="text" value={orderSearch} onChange={e => setOrderSearch(e.target.value)}
                  placeholder={`${t('common.search')}...`} className="pl-8 pr-3 py-1.5 bg-surface-inset border border-line-hover rounded-lg text-xs text-content placeholder-content-dim focus:outline-none w-full" />
              </div>
            )}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wider border-b border-line">
                <th className="py-2 pl-4 w-6"></th>
                <OrdSortHeader col="picking_name" align="left">{t('cm.order')}</OrdSortHeader>
                <OrdSortHeader col="datum">{t('cm.date')}</OrdSortHeader>
                <OrdSortHeader col="skus">SKUs</OrdSortHeader>
                <OrdSortHeader col="artikel">{t('cm.articles_short')}</OrdSortHeader>
                <OrdSortHeader col="kosten_personal">{t('cm.h_cost')}</OrdSortHeader>
                <OrdSortHeader col="umsatz_gesamt">{t('cm.revenue')}</OrdSortHeader>
                <OrdSortHeader col="kosten_gesamt">{t('cm.costs')}</OrdSortHeader>
                <OrdSortHeader col="db_gesamt">{t('cm.cm_short')}</OrdSortHeader>
                <OrdSortHeader col="db_prozent">{t('cm.cm_pct')}</OrdSortHeader>
              </tr>
            </thead>
            <tbody>
              {sortedOrders.map(o => {
                const isExpanded = expandedOrder === o.picking_name;
                const noTime = !o.pick_zeit_min && !o.pack_zeit_min;
                const hasPick = o.has_pick_times ?? (o.pick_zeit_min > 0);
                const hasPack = o.has_pack_times ?? (o.pack_typ !== 'keine_daten' && o.pack_zeit_min > 0);
                const isBillingPending = o._billingPending;
                return (
                  <Fragment key={o.picking_name}>
                    <tr onClick={() => setExpandedOrder(isExpanded ? null : o.picking_name)}
                      className={`border-b border-line hover:bg-surface-hover transition-colors cursor-pointer ${isExpanded ? 'bg-surface-hover' : ''}`}>
                      <td className="py-2 pl-4">
                        <svg className={`w-3 h-3 text-[var(--text-secondary)] transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                          fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path d="M9 5l7 7-7 7" />
                        </svg>
                      </td>
                      <td className="py-2 px-2 text-xs font-mono text-content">
                        <span className="inline-flex items-center gap-1.5">
                          {o.picking_name}
                          {o.is_sop && <Badge variant="info" className="text-[8px] px-1 py-0">SOP</Badge>}
                          {o.pack_typ === 'bulk_pauschale' && <Badge variant="warning" className="text-[8px] px-1 py-0">BP</Badge>}
                          {o.wave_typ === 'mixed' && <Badge variant="warning" className="text-[8px] px-1 py-0" title={`Mixed-Wave: ${fmt(o.pick_anteil, 0)}% ${t('cm.tooltip.share')}`}>&#9889; {fmt(o.pick_anteil, 0)}%</Badge>}
                        </span>
                      </td>
                      <td className="py-2 px-2 text-right text-xs font-mono text-content-secondary">{fmtDate(o.datum)}</td>
                      <td className="py-2 px-2 text-right text-xs font-mono text-content-tertiary">{o.skus}</td>
                      <td className="py-2 px-2 text-right text-xs font-mono text-content-tertiary">{o.artikel}</td>
                      <td className="py-2 px-2 text-right text-xs font-mono text-content-secondary" onClick={e => e.stopPropagation()}>
                        <span className="inline-flex items-center gap-1">
                          {fmtEur(o.kosten_personal)}
                          <InfoBadge warn={noTime}><HandlingCostTip o={o} /></InfoBadge>
                        </span>
                        {(!hasPick && !hasPack) && (
                          <span className="block text-[9px] text-orange-500 font-semibold mt-0.5" title={t('cm.no_pick_pack_times_title')}>
                            <AlertTriangle className="w-3 h-3 inline mr-0.5" />{t('cm.no_times')}
                          </span>
                        )}
                        {(!hasPick && hasPack) && (
                          <span className="block text-[9px] text-orange-500 font-semibold mt-0.5" title={t('cm.no_pick_times_title')}>
                            <AlertTriangle className="w-3 h-3 inline mr-0.5" />{t('cm.no_pick')}
                          </span>
                        )}
                        {(hasPick && !hasPack) && (
                          <span className="block text-[9px] text-orange-500 font-semibold mt-0.5" title={t('cm.no_pack_times_title')}>
                            <AlertTriangle className="w-3 h-3 inline mr-0.5" />{t('cm.no_pack')}
                          </span>
                        )}
                      </td>
                      <td className="py-2 px-2 text-right text-xs font-mono font-bold text-content">
                        {isBillingPending
                          ? <span className="text-[var(--text-tertiary,#6B7280)] dark:text-[var(--text-secondary,#4B5563)] font-normal">⏳ pending</span>
                          : fmtEur(o.umsatz_gesamt)}
                      </td>
                      <td className="py-2 px-2 text-right text-xs font-mono text-content-secondary">{fmtEur(o.kosten_gesamt)}</td>
                      <td className="py-2 px-2 text-right text-xs font-mono font-bold" style={{ color: 'var(--text-primary)' }}>
                        {isBillingPending
                          ? <span className="text-[var(--text-tertiary,#6B7280)] dark:text-[var(--text-secondary,#4B5563)] font-normal">⏳</span>
                          : <>{fmtEur(o.db_gesamt)}{noTime && <span className="ml-0.5 text-[9px] text-yellow-400" title={t('cm.cm_without_staff_costs')}>*</span>}</>}
                      </td>
                      <td className="py-2 px-2 text-right pr-4">
                        {isBillingPending
                          ? <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-bold font-mono bg-gray-500/15 text-[var(--text-tertiary,#6B7280)] dark:text-[var(--text-secondary,#4B5563)]">⏳</span>
                          : <span className="inline-flex items-center justify-end gap-1.5 text-[10px] font-bold font-mono text-[var(--text-primary)]">
                              <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: margeColor(o.db_prozent, ziele) }} />
                              {fmt(o.db_prozent, 1)}%
                            </span>}
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td colSpan={10} className="p-0">
                          <OrderDetail pickingName={o.picking_name} sollStammzeit={o.soll_stammzeit} sollTop25={o.soll_top25} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
              {sortedOrders.length === 0 && (
                <tr><td colSpan={10} className="py-8 text-center text-[var(--text-tertiary)] text-sm">{t('cm.no_orders_in_period')}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   Main Component
   ═══════════════════════════════════════════════════════════ */
export default function Deckungsbeitrag() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const isAdmin = user?.rolle === 'admin' || user?.rolle === 'manager';
  const [von, setVon] = useState('');
  const [bis, setBis] = useState('');
  const [preset, setPreset] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [computing, setComputing] = useState(false);
  const [computeResult, setComputeResult] = useState(null);
  const [error, setError] = useState(null);
  const [sortCol, setSortCol] = useState('umsatz');
  const [sortDir, setSortDir] = useState('desc');
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useTabParam('tabelle');
  const [selectedKunde, setSelectedKunde] = useState(null);
  const [showKatModal, setShowKatModal] = useState(false);
  const [katChanged, setKatChanged] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [jobProgress, setJobProgress] = useState(null);
  const [qualitySummary, setQualitySummary] = useState(null);

  useEffect(() => {
    const range = getDateRange('3m');
    setVon(range.von);
    setBis(range.bis);
    setPreset('3m');
  }, []);

  useEffect(() => {
    if (!von || !bis) return;
    setLoading(true);
    setError(null);
    fetch(`${API}/api/deckungsbeitrag/uebersicht?von=${von}&bis=${bis}`, { credentials: 'include' })
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [von, bis]);

  // Fetch quality summary when data loads
  useEffect(() => {
    if (!von || !bis) return;
    fetch(`${API}/api/reports/quality-summary?von=${von}&bis=${bis}`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => setQualitySummary(d))
      .catch(() => setQualitySummary(null));
  }, [von, bis, data]);

  const handleRangeChange = useCallback(({ von: v, bis: b, preset: p }) => {
    setVon(v); setBis(b); setPreset(p);
    setSelectedKunde(null);
  }, []);

  const triggerCompute = useCallback(async () => {
    if (!von || !bis) return;
    setComputing(true);
    setComputeResult(null);
    setKatChanged(false);
    setJobProgress(null);
    try {
      const r = await fetch(`${API}/api/jobs/deckungsbeitrag/berechnen`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ von, bis }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const result = await r.json();
      if (result.error) {
        if (result.running) { setError(t('cm.calc_already_running')); }
        else { setError(result.error); }
        setComputing(false);
        return;
      }
      setJobId(result.job_id);
    } catch (e) {
      setError(e.message);
      setComputing(false);
    }
  }, [von, bis]);

  // Poll job status
  useEffect(() => {
    if (!jobId) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await fetch(`${API}/api/jobs/${jobId}/status`, { credentials: 'include' });
        if (!r.ok || cancelled) return;
        const job = await r.json();
        if (job.status === 'completed') {
          setJobId(null);
          setJobProgress(null);
          setComputing(false);
          setComputeResult(job.result || { ok: true });
          // Reload overview data
          const r2 = await fetch(`${API}/api/deckungsbeitrag/uebersicht?von=${von}&bis=${bis}`, { credentials: 'include' });
          if (r2.ok) setData(await r2.json());
          return;
        }
        if (job.status === 'failed') {
          setJobId(null);
          setJobProgress(null);
          setComputing(false);
          setError(job.error || t('cm.calc_failed'));
          return;
        }
        // Still running
        setJobProgress(job.progress || {});
        if (!cancelled) setTimeout(poll, 3000);
      } catch {
        if (!cancelled) setTimeout(poll, 5000);
      }
    };
    const timer = setTimeout(poll, 1000);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [jobId, von, bis]);

  const toggleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('desc'); }
  };

  const sortedKunden = useMemo(() => {
    if (!data?.kunden) return [];
    let arr = [...data.kunden];
    if (search) { const q = search.toLowerCase(); arr = arr.filter(k => k.name?.toLowerCase().includes(q)); }
    arr.sort((a, b) => {
      const va = a[sortCol] ?? 0, vb = b[sortCol] ?? 0;
      if (typeof va === 'string') return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
      return sortDir === 'asc' ? va - vb : vb - va;
    });
    return arr;
  }, [data, sortCol, sortDir, search]);

  const ziele = data?.margenziele;

  const SortHeader = ({ col, children, align = 'left' }) => (
    <th className={`py-3 cursor-pointer select-none hover:text-content-secondary transition-colors ${align === 'right' ? 'text-right' : 'text-left'} ${col === 'name' ? 'pl-4' : ''} ${col === 'marge_pct' ? 'pr-4' : ''}`}
      onClick={() => toggleSort(col)}>
      <span className="inline-flex items-center gap-1">
        {children}
        {sortCol === col && (
          <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path d={sortDir === 'asc' ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'} />
          </svg>
        )}
      </span>
    </th>
  );

  return (
    <div className="p-page">
      {/* Header */}
      <PageHeader
        title={t('cm.title')}
        subtitle={t('cm.subtitle')}
        actions={
          <>
            {isAdmin && (
              <Button variant="ghost" size="sm" icon={Settings} onClick={() => setShowKatModal(true)}>{t('cm.categories_btn')}</Button>
            )}
            <Button variant="secondary" size="sm" icon={RefreshCw} onClick={triggerCompute} loading={computing}>
              {computing ? (jobProgress?.message || t('cm.calculating')) + (jobProgress?.percent ? ` ${jobProgress.percent}%` : '') : t('cm.recalculate')}
            </Button>
          </>
        }
      />

      {/* Kat changed banner */}
      {katChanged && (
        <div className="mb-4 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-card text-sm text-yellow-400 flex items-center justify-between mt-section">
          <span>{t('cm.mapping_changed')}</span>
          <Button variant="ghost" size="xs" onClick={triggerCompute} loading={computing} className="text-yellow-400">
            {computing ? t('cm.calculating') : t('cm.recalculate')}
          </Button>
        </div>
      )}

      {/* Compute result banner */}
      {computeResult && (
        <div className="mb-4 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-card text-sm text-emerald-400 flex items-center justify-between mt-section">
          <span>{computeResult.computed} {t('cm.orders_computed')}, {computeResult.billing_matched} {t('cm.with_billing_data')}</span>
          <button onClick={() => setComputeResult(null)} className="text-emerald-400/60 hover:text-emerald-400">&times;</button>
        </div>
      )}

      {/* Progress bar */}
      {computing && jobProgress?.percent > 0 && (
        <div className="mb-4">
          <div className="h-2 bg-surface-inset rounded-full overflow-hidden">
            <div className="h-full bg-emerald-500 rounded-full transition-all duration-500" style={{ width: `${jobProgress.percent}%` }} />
          </div>
          <div className="mt-1 text-[10px] text-[var(--text-tertiary)]">{jobProgress.message || t('cm.calculating')}</div>
        </div>
      )}

      {/* Quality warning banner */}
      {!computing && qualitySummary?.kunden?.some(k => k.ampel === 'rot') && (
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-card text-sm text-red-400 flex items-center justify-between">
          <span>
            {qualitySummary.kunden.filter(k => k.ampel === 'rot').length} {t('cm.customers_incomplete_data')}
          </span>
          <Button variant="ghost" size="xs" onClick={() => window.location.href = '/reports'} className="text-red-400">{t('cm.show_reports')}</Button>
        </div>
      )}

      {/* Filter Bar */}
      <DateRangeFilter
        von={von} bis={bis} preset={preset}
        onChange={handleRangeChange}
        className="mb-section mt-section"
        timestamp={data?.letzte_berechnung ? new Date(data.letzte_berechnung).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : null}
      />

      {error && (
        <div className="mb-section p-3 bg-red-500/10 border border-red-500/20 rounded-card text-sm text-red-400">{t('common.error')}: {error}</div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin h-6 w-6 border-2 border-primary-500 border-t-transparent rounded-full" />
          <span className="ml-3 text-sm text-[var(--text-secondary)]">{t('cm.loading_cm')}</span>
        </div>
      )}

      {!loading && !error && (!data?.kunden?.length) && (
        <Card className="mb-section text-center">
          <div className="flex flex-col items-center gap-3 py-4">
            <RefreshCw className="w-12 h-12 text-[var(--text-tertiary)]" />
            <div>
              <div className="text-base font-medium text-content mb-1">{t('cm.no_cm_calculated')}</div>
              <div className="text-sm text-[var(--text-secondary)] mb-4">{t('cm.no_cm_hint')}</div>
              <Button variant="primary" size="md" icon={RefreshCw} onClick={triggerCompute} loading={computing}>
                {computing ? t('cm.calculating') : t('cm.calculate_now')}
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Selected Customer Detail */}
      {!loading && selectedKunde && (
        <KundenDetailView
          kundeId={selectedKunde.kunde_id}
          kundeName={selectedKunde.name}
          von={von} bis={bis} ziele={ziele}
          isAdmin={isAdmin}
          onBack={() => setSelectedKunde(null)}
        />
      )}

      {/* Overview */}
      {!loading && !selectedKunde && data?.kunden?.length > 0 && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-6 gap-3 mb-section mt-section">
            <KpiCard label={t('common.customers')} value={data.kunden.length} />
            <KpiCard label={t('cm.orders')} value={fmt(data.totals.auftraege)} />
            <KpiCard label={t('cm.revenue')} value={fmtEur(data.totals.umsatz)} />
            <KpiCard label={t('cm.costs')} value={fmtEur(data.totals.kosten)} />
            <KpiCard label={t('cm.cm_total')} value={fmtEur(data.totals.db)} />
            <KpiCard label={t('cm.cm_margin')} value={`${fmt(data.totals.marge_pct, 1)}%`}
              trend={data.totals.marge_pct >= (ziele?.margin_target_good || 25) ? t('cm.status.goal_reached') : data.totals.marge_pct >= (ziele?.margin_target_min || 15) ? t('cm.status.acceptable') : t('cm.status.below_target')}
              trendUp={data.totals.marge_pct >= (ziele?.margin_target_min || 15) ? true : false} />
          </div>

          {/* Mixed-Wave Stats */}
          {data.mixed_wave_stats?.mixed_waves > 0 && (
            <div className="mb-section p-3 bg-orange-500/5 border border-orange-500/15 rounded-card text-xs text-content-secondary flex items-center gap-2">
              <span className="text-orange-400 font-bold">&#9889;</span>
              <span>Mixed-Waves: {fmt(data.mixed_wave_stats.mixed_waves)} {t('cm.of')} {fmt(data.mixed_wave_stats.total_waves)} Waves ({fmt(data.mixed_wave_stats.mixed_pct, 1)}%)</span>
              <span className="text-[var(--text-tertiary)]">—</span>
              <span>{fmt(data.mixed_wave_stats.betroffene_kunden)} {t('cm.affected_customers')}</span>
              <span className="text-[var(--text-tertiary)]">—</span>
              <span className="text-[var(--text-secondary)]">{t('cm.method_picks_share')}</span>
            </div>
          )}

          <div className="inline-flex bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] gap-1 border border-[var(--border-default,#E5E7EB)] mb-section">
            {[
              { key: 'tabelle', label: t('cm.tab.customer_overview'), badge: data.kunden.length },
              { key: 'charts', label: t('cm.tab.charts') },
              { key: 'personalkosten', label: t('efficiency.laborCost') },
              { key: 'kpi', label: t('cm.kpiAnalyse') },
            ].map(tab => (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                className={`px-4 py-2 rounded-[8px] text-sm font-medium transition-all flex items-center gap-2 ${
                  activeTab === tab.key
                    ? 'bg-white shadow-sm text-[var(--text-primary)] font-semibold dark:bg-[var(--bg-card)]'
                    : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
                }`}>
                {tab.label}
                {tab.badge != null && <Badge variant="default" className="font-mono text-[10px]">{tab.badge}</Badge>}
              </button>
            ))}
          </div>

          <Card padding="lg">
            {activeTab === 'tabelle' && (
              <>
                {data.kunden.length > 5 && (
                  <div className="mb-4">
                    <div className="relative w-64">
                      <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-secondary)]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" /></svg>
                      <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                        placeholder={`${t('cm.search_customer')}...`} className="pl-9 pr-3 py-2 bg-surface-inset border border-line-hover rounded-lg text-sm text-content placeholder-content-dim focus:outline-none w-full" />
                    </div>
                  </div>
                )}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wider border-b border-line">
                        <th className="py-3 pl-4 text-left w-8"></th>
                        <SortHeader col="name">{t('cm.customer')}</SortHeader>
                        <th className="py-3 text-center w-8" title={t('cm.data_quality')}>Q</th>
                        <SortHeader col="auftraege" align="right">{t('cm.orders')}</SortHeader>
                        <SortHeader col="sop_pct" align="right">SOP%</SortHeader>
                        <SortHeader col="umsatz" align="right">{t('cm.revenue')}</SortHeader>
                        <SortHeader col="kosten" align="right">{t('cm.costs')}</SortHeader>
                        <SortHeader col="db" align="right">{t('cm.cm_short')}</SortHeader>
                        <SortHeader col="marge_pct" align="right">{t('cm.cm_pct')}</SortHeader>
                        <SortHeader col="soll_stammzeit_db_pct" align="right">{t('cm.dbSollStamm')}</SortHeader>
                        <SortHeader col="soll_top25_db_pct" align="right">{t('cm.dbSollTop25')}</SortHeader>
                        <SortHeader col="einsparpotential_top25" align="right">{t('cm.einsparpotential')}</SortHeader>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedKunden.map(k => (
                        <tr key={k.kunde_id}
                          onClick={() => k.kunde_id > 0 ? setSelectedKunde({ kunde_id: k.kunde_id, name: k.name }) : null}
                          className={`border-b border-line hover:bg-surface-hover transition-colors ${k.kunde_id > 0 ? 'cursor-pointer' : ''}`}>
                          <td className="py-3 pl-4 text-center">
                            <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: k.all_billing_pending ? '#6b7280' : margeColor(k.marge_pct, ziele) }} />
                          </td>
                          <td className="py-3">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-content">{k.name}</span>
                              {k.segment && (
                                <Badge variant={`segment-${k.segment.toLowerCase()}`} className="text-[10px]">{k.segment}</Badge>
                              )}
                              {k.mixed_auftraege > 0 && (
                                <Badge variant="warning" className="text-[10px]" title={`${k.mixed_auftraege} ${t('cm.orders_from_mixed')}`}>&#9889; {fmt(k.mixed_pct, 0)}% mixed</Badge>
                              )}
                            </div>
                          </td>
                          <td className="py-3 text-center">
                            {(() => {
                              const qk = qualitySummary?.kunden?.find(q => q.kunde_id === k.kunde_id);
                              if (!qk) return <span className="text-[var(--text-tertiary)]">-</span>;
                              const ampelColor = qk.ampel === 'gruen' ? '#34d399' : qk.ampel === 'gelb' ? '#fbbf24' : '#ef4444';
                              return (
                                <span className="relative group">
                                  <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: ampelColor }} />
                                  <span className="absolute hidden group-hover:block z-50 bg-[#1e1e2e] border border-line rounded-lg shadow-2xl p-2 text-[10px] text-content-secondary whitespace-nowrap -translate-x-1/2 left-1/2 bottom-full mb-1">
                                    Billing: {qk.billing_pct?.toFixed(0)}% | Pick: {qk.pick_pct?.toFixed(0)}% | Pack: {qk.pack_pct?.toFixed(0)}%
                                    {qk.warnungen?.length > 0 && qk.warnungen.map((w, i) => <span key={i} className="block text-yellow-400">{w}</span>)}
                                  </span>
                                </span>
                              );
                            })()}
                          </td>
                          <td className="py-3 text-right font-mono text-xs text-content-secondary whitespace-nowrap">{fmt(k.auftraege)}</td>
                          <td className="py-3 text-right text-xs font-mono">
                            {k.sop_pct > 0 ? (
                              <span className="text-[var(--text-primary)]">{fmt(k.sop_pct, 0)}%</span>
                            ) : (
                              <span className="text-[var(--text-tertiary)]">–</span>
                            )}
                          </td>
                          <td className="py-3 text-right font-mono text-xs text-content-secondary whitespace-nowrap">
                            {k.all_billing_pending
                              ? <span className="text-[var(--text-tertiary,#6B7280)] dark:text-[var(--text-secondary,#4B5563)] font-normal">⏳ pending</span>
                              : (() => { const c = fmtEurCompact(k.umsatz); return <><span title={c.full}>{c.text}</span>{k.billing_pending_count > 0 && <div className="text-[9px] text-[var(--text-tertiary,#6B7280)] dark:text-[var(--text-secondary,#4B5563)] font-normal">+ {k.billing_pending_count} {t('cm.without_billing')}</div>}</>; })()}
                          </td>
                          <td className="py-3 text-right font-mono text-xs text-content-secondary whitespace-nowrap" title={fmtEurCompact(k.kosten).full}>
                            {fmtEurCompact(k.kosten).text}
                            {k.revshare_abgabe > 0 && <div className="text-[9px] text-primary-400 font-normal">RS {fmtEurCompact(k.revshare_abgabe).text}</div>}
                          </td>
                          <td className="py-3 text-right font-mono text-xs font-bold text-content whitespace-nowrap">
                            {k.all_billing_pending
                              ? <span className="text-[var(--text-tertiary,#6B7280)] dark:text-[var(--text-secondary,#4B5563)] font-normal">⏳</span>
                              : (() => { const c = fmtEurCompact(k.db); return <><span title={c.full}>{c.text}</span>{k.billing_pending_count > 0 && <div className="text-[9px] text-[var(--text-tertiary,#6B7280)] dark:text-[var(--text-secondary,#4B5563)] font-normal">+ {k.billing_pending_count} {t('cm.without_billing')}</div>}</>; })()}
                          </td>
                          <td className="py-3 text-right">
                            {k.all_billing_pending
                              ? <Badge variant="default" className="font-mono">⏳</Badge>
                              : <span className="inline-flex items-center justify-end gap-1.5 text-xs font-bold font-mono text-[var(--text-primary)]">
                                  <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: margeColor(k.marge_pct, ziele) }} />
                                  {fmt(k.marge_pct, 1)}%
                                </span>}
                          </td>
                          <td className="py-3 text-right text-xs font-mono">
                            {k.soll_stammzeit_db_pct != null ? (
                              <span className="inline-flex items-center justify-end gap-1.5 text-[var(--text-primary)]">
                                <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: margeColor(k.soll_stammzeit_db_pct, ziele) }} />
                                {fmt(k.soll_stammzeit_db_pct, 1)}%
                              </span>
                            ) : <span className="text-[var(--text-tertiary)]">–</span>}
                          </td>
                          <td className="py-3 text-right text-xs font-mono">
                            {k.soll_top25_db_pct != null ? (
                              <span className="inline-flex items-center justify-end gap-1.5 text-[var(--text-primary)]">
                                <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: margeColor(k.soll_top25_db_pct, ziele) }} />
                                {fmt(k.soll_top25_db_pct, 1)}%
                              </span>
                            ) : <span className="text-[var(--text-tertiary)]">–</span>}
                          </td>
                          <td className="py-3 text-right pr-4 text-xs font-mono whitespace-nowrap">
                            {k.einsparpotential_top25 != null ? (() => {
                              const c = fmtEurCompact(k.einsparpotential_top25);
                              return (
                                <span className={k.einsparpotential_top25 > 0 ? 'text-primary-400 font-bold' : k.einsparpotential_top25 < 0 ? 'text-red-400' : 'text-[var(--text-tertiary)]'}
                                  title={c.full}>
                                  {k.einsparpotential_top25 > 0 ? '+' : ''}{c.text}
                                </span>
                              );
                            })() : <span className="text-[var(--text-tertiary)]">–</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {activeTab === 'charts' && (
              <div className="space-y-8">
                <div className="bg-surface-inset/50 rounded-lg p-4"><ScatterPlot data={sortedKunden} ziele={ziele} /></div>
                <div>
                  <div className="text-[10px] text-[var(--text-secondary)] font-semibold uppercase tracking-wider mb-3">{t('cm.chart.top10_by_cm')}</div>
                  <div className="space-y-2">
                    {[...sortedKunden].sort((a, b) => b.db - a.db).slice(0, 10).map((k, i) => {
                      const maxDb = Math.max(...data.kunden.map(x => Math.abs(x.db)), 1);
                      const pct = Math.abs(k.db) / maxDb * 100;
                      return (
                        <div key={k.kunde_id} className="flex items-center gap-3 cursor-pointer hover:bg-surface-hover rounded-lg px-2 py-1 transition-colors"
                          onClick={() => k.kunde_id > 0 && setSelectedKunde({ kunde_id: k.kunde_id, name: k.name })}>
                          <span className="text-xs text-[var(--text-secondary)] w-4 text-right font-mono">{i + 1}</span>
                          <span className="text-xs text-content w-40 truncate">{k.name}</span>
                          <div className="flex-1 h-5 bg-surface-inset rounded-full overflow-hidden">
                            <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: margeColor(k.marge_pct, ziele), opacity: 0.7 }} />
                          </div>
                          <span className="text-xs font-mono font-bold w-20 text-right text-content">{fmtEur(k.db)}</span>
                          <span className="inline-flex items-center gap-1.5 text-[10px] font-mono font-bold text-[var(--text-primary)]">
                            <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: margeColor(k.marge_pct, ziele) }} />
                            {fmt(k.marge_pct, 1)}%
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
                {data.trend?.length >= 2 && (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="bg-surface-inset/50 rounded-lg p-4"><TrendChart data={data.trend} dataKey="marge_pct" label={t('cm.chart.cm_margin_monthly')} color="#34d399" /></div>
                    <div className="bg-surface-inset/50 rounded-lg p-4"><TrendChart data={data.trend} dataKey="db" label={t('cm.chart.cm_abs_monthly')} color="#338edc" /></div>
                  </div>
                )}
              </div>
            )}
          </Card>

          {activeTab === 'personalkosten' && (
            <PersonalkostenTab von={von} bis={bis} />
          )}

          {activeTab === 'kpi' && (
            <KpiAnalyseTab von={von} bis={bis} />
          )}

          <div className="mt-4 flex flex-wrap items-center gap-3 text-[10px] text-[var(--text-tertiary)]">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-400" /> &ge; {ziele?.margin_target_good || 25}% {t('cm.legend.target')}</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-yellow-400" /> &ge; {ziele?.margin_target_min || 15}% {t('cm.status.ok')}</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-orange-400" /> &ge; {ziele?.margin_target_bad || 10}% {t('cm.status.warning')}</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-400" /> &lt; {ziele?.margin_target_bad || 10}% {t('cm.status.critical')}</span>
          </div>
        </>
      )}

      {/* Kategorien Modal */}
      <KategorienModal open={showKatModal} onClose={() => setShowKatModal(false)} onChanged={() => setKatChanged(true)} />
    </div>
  );
}

/* ─── Personalkosten Tab (compact view) ─── */
function PersonalkostenTab({ von, bis }) {
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!von || !bis) return;
    setLoading(true);
    const params = new URLSearchParams({ von, bis, type: 'pack' });
    fetch(`${API}/api/performance/ma-effizienz/ranking?${params}`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [von, bis]);

  if (loading) return <Card padding="lg"><div className="flex justify-center py-8"><div className="animate-spin h-5 w-5 border-2 border-primary-500 border-t-transparent rounded-full" /></div></Card>;
  if (!data?.ranking?.length) return <Card padding="lg"><div className="text-sm text-[var(--text-tertiary)] py-8 text-center">{t('efficiency.noData')}</div></Card>;

  const s = data.summary;
  const totalKosten = (s.total_brutto_stunden * 27);
  const top10 = data.ranking.slice(0, 10);
  const maxNetto = top10.length > 0 ? Math.max(...top10.map(r => r.netto_effizienz), 0.01) : 1;

  function pkBarColor(rel) {
    if (rel > 120) return 'bg-emerald-500/20 dark:bg-emerald-500/15';
    if (rel < 80) return 'bg-amber-500/20 dark:bg-amber-500/15';
    return 'bg-gray-300/30 dark:bg-gray-500/15';
  }

  return (
    <Card padding="lg">
      {/* 3 KPI Cards */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="text-center">
          <div className="flex items-center justify-center gap-2 mb-1">
            <DollarSign size={20} className="text-[#10B981]" />
            <div className="text-[10px] font-semibold uppercase text-[var(--text-secondary)] tracking-wider">{t('efficiency.laborCost')}</div>
          </div>
          <div className="text-2xl font-bold text-content">{fmtEur(totalKosten)}</div>
        </div>
        <div className="text-center">
          <div className="flex items-center justify-center gap-2 mb-1">
            <Activity size={20} className="text-[#10B981]" />
            <div className="text-[10px] font-semibold uppercase text-[var(--text-secondary)] tracking-wider">{t('efficiency.avgCostPerArticle')}</div>
          </div>
          <div className="text-2xl font-bold text-content">{s.total_artikel > 0 ? fmtEur(totalKosten / s.total_artikel) : '–'}</div>
        </div>
        <div className="text-center">
          <div className="flex items-center justify-center gap-2 mb-1">
            <Gauge size={20} className="text-[#338edc]" />
            <div className="text-[10px] font-semibold uppercase text-[var(--text-secondary)] tracking-wider">{t('efficiency.nettoEff')}</div>
          </div>
          <div className="text-2xl font-bold text-content">{s.team_netto_avg ? Number(s.team_netto_avg).toFixed(1).replace('.', ',') : '–'} <span className="text-sm font-normal text-[var(--text-secondary)]">{t('efficiency.artPerMin')}</span></div>
        </div>
      </div>

      {/* Mini ranking top 10 */}
      <div className="text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wider mb-2">Top 10 — {t('efficiency.netEff')}</div>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-line">
            <th className="px-2 py-1.5 text-[11px] font-semibold uppercase text-[var(--text-tertiary)] text-center w-8">#</th>
            <th className="px-2 py-1.5 text-[11px] font-semibold uppercase text-[var(--text-tertiary)] text-left">{t('idleTime.employee')}</th>
            <th className="px-2 py-1.5 text-[11px] font-semibold uppercase text-[var(--text-tertiary)] text-right">{t('efficiency.activities')}</th>
            <th className="px-2 py-1.5 text-[11px] font-semibold uppercase text-[var(--text-tertiary)] text-right">{t('efficiency.articles')}</th>
            <th className="px-2 py-1.5 text-[11px] font-semibold uppercase text-[var(--text-tertiary)] text-right">{t('efficiency.grossEff')}</th>
            <th className="px-2 py-1.5 text-[11px] font-semibold uppercase text-[var(--text-tertiary)] text-right">{t('efficiency.netEff')}</th>
            <th className="px-2 py-1.5 text-[11px] font-semibold uppercase text-[var(--text-tertiary)] text-right">{t('efficiency.relative')}</th>
            <th className="px-2 py-1.5 text-[11px] font-semibold uppercase text-[var(--text-tertiary)] text-right">{t('efficiency.costPerArticle')}</th>
          </tr>
        </thead>
        <tbody>
          {top10.map(r => (
            <tr key={r.employee_name} className="border-b border-line-subtle hover:bg-surface-hover">
              <td className="px-2 py-1.5 text-center text-[var(--text-secondary)] font-mono text-xs">{r.rank}</td>
              <td className="px-2 py-1.5 text-left text-content font-medium text-xs">
                {r.employee_name}
                {r.is_shared_account && <span className="ml-1 px-1 py-0.5 rounded text-[8px] font-semibold bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">SHARED</span>}
              </td>
              <td className="px-2 py-1.5 text-right font-mono text-content text-xs">{fmt(r.vorgaenge)}</td>
              <td className="px-2 py-1.5 text-right font-mono text-content text-xs">{fmt(r.artikel_gesamt)}</td>
              <td className="px-2 py-1.5 text-right font-mono text-content text-xs">{Number(r.brutto_effizienz).toFixed(1).replace('.', ',')}</td>
              <td className="px-2 py-1.5 text-right relative overflow-hidden">
                <div className={`absolute inset-y-0 left-0 ${pkBarColor(r.relative_effizienz)} transition-all`}
                  style={{ width: `${Math.min(100, (r.netto_effizienz / maxNetto) * 100)}%` }} />
                <span className="relative font-mono font-bold text-content text-xs">{Number(r.netto_effizienz).toFixed(1).replace('.', ',')}</span>
              </td>
              <td className="px-2 py-1.5 text-right relative overflow-hidden">
                <div className={`absolute inset-y-0 left-0 ${pkBarColor(r.relative_effizienz)} transition-all`}
                  style={{ width: `${Math.min(r.relative_effizienz, 200) / 2}%` }} />
                <span className="relative font-mono text-content text-xs">{r.relative_effizienz >= 200 ? '200%+' : `${r.relative_effizienz}%`}</span>
              </td>
              <td className="px-2 py-1.5 text-right font-mono text-content text-xs">{fmtEur(r.kosten_pro_artikel)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Link to detailed analysis */}
      <div className="mt-4 text-center">
        <Link to="/performance" className="inline-flex items-center gap-1 text-sm text-primary-500 hover:text-primary-400 font-medium">
          {t('efficiency.detailedAnalysis')}
        </Link>
      </div>
    </Card>
  );
}

/* ─── KPI-Analyse Tab ─── */

function dbProStundeColor(val) {
  if (val >= 20) return '#16A34A';
  if (val >= 10) return '#F59E0B';
  return '#EF4444';
}

function effizienzBadge(val) {
  if (val >= 100) return { bg: '#ECFDF5', text: '#065F46', darkBg: 'rgba(16,185,129,0.12)', darkText: '#34d399', arrow: '↑' };
  if (val >= 80)  return { bg: '#FFFBEB', text: '#92400E', darkBg: 'rgba(245,158,11,0.12)', darkText: '#fbbf24', arrow: '→' };
  return { bg: '#FEF2F2', text: '#991B1B', darkBg: 'rgba(239,68,68,0.12)', darkText: '#f87171', arrow: '↓' };
}

function komplexitaetLabel(val, t) {
  if (val < 1) return t('cm.einfach');
  if (val < 3) return t('cm.mittel');
  return t('cm.komplex');
}

function KpiAnalyseTab({ von, bis }) {
  const { t, locale } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortCol, setSortCol] = useState('db_pro_stunde');
  const [sortDir, setSortDir] = useState('desc');

  useEffect(() => {
    if (!von || !bis) return;
    setLoading(true);
    fetch(`${API}/api/deckungsbeitrag/kunden-kpis?von=${von}&bis=${bis}`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [von, bis]);

  const sorted = useMemo(() => {
    if (!data?.kunden) return [];
    return [...data.kunden].sort((a, b) => {
      const av = a[sortCol] ?? -Infinity;
      const bv = b[sortCol] ?? -Infinity;
      return sortDir === 'desc' ? bv - av : av - bv;
    });
  }, [data, sortCol, sortDir]);

  const toggleSort = col => {
    if (sortCol === col) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortCol(col); setSortDir('desc'); }
  };

  const sortIcon = col => sortCol === col ? (sortDir === 'desc' ? ' ▾' : ' ▴') : '';

  if (loading) return <Card padding="lg"><div className="flex justify-center py-8"><div className="animate-spin h-5 w-5 border-2 border-primary-500 border-t-transparent rounded-full" /></div></Card>;
  if (!data?.kunden?.length) return <Card padding="lg"><div className="text-sm text-[var(--text-tertiary)] py-8 text-center">{t('common.noData')}</div></Card>;

  const tot = data.totals;
  const maxDbH = Math.max(...sorted.map(k => k.db_pro_stunde || 0), 1);

  return (
    <Card padding="lg">
      {/* 3 Summary KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-2">
            <Timer size={20} className="text-[#10B981]" />
            <p className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-secondary)]">
              {t('cm.komplexitaetIndex')}
            </p>
          </div>
          <p className="text-2xl font-bold text-[var(--text-primary)]">
            {tot.avg_komplexitaet?.toFixed(1).replace('.', ',')} min
          </p>
          <p className="text-xs text-[var(--text-tertiary)] mt-1">
            {komplexitaetLabel(tot.avg_komplexitaet || 0, t)} — {t('cm.perOrder')}
          </p>
        </div>
        <div className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-2">
            <Gauge size={20} className="text-[#338edc]" />
            <p className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-secondary)]">
              {t('cm.effizienzIndex')}
            </p>
          </div>
          <p className="text-2xl font-bold text-[var(--text-primary)]">
            {tot.avg_effizienz != null ? `${tot.avg_effizienz.toFixed(0)}%` : '–'}
          </p>
          <p className="text-xs text-[var(--text-tertiary)] mt-1">
            {t('cm.sollVsIst')}
          </p>
        </div>
        <div className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp size={20} className="text-[#10B981]" />
            <p className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-secondary)]">
              {t('cm.dbProStunde')}
            </p>
          </div>
          <p className="text-2xl font-bold text-[var(--text-primary)]">
            {tot.avg_db_pro_stunde != null ? `${tot.avg_db_pro_stunde.toFixed(2).replace('.', ',')} €/h` : '–'}
          </p>
          <p className="text-xs mt-1" style={{ color: dbProStundeColor(tot.avg_db_pro_stunde || 0) }}>
            {(tot.avg_db_pro_stunde || 0) >= 20 ? t('cm.profitabel') : (tot.avg_db_pro_stunde || 0) >= 10 ? t('cm.grenzwertig') : t('cm.kritisch')}
          </p>
        </div>
      </div>

      {/* Kunden-Ranking Tabelle */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-line">
              <th className="px-3 py-2 text-[10px] font-semibold uppercase text-[var(--text-tertiary)] text-left">{t('common.customer')}</th>
              <th className="px-3 py-2 text-[10px] font-semibold uppercase text-[var(--text-tertiary)] text-right cursor-pointer select-none" onClick={() => toggleSort('auftraege')}>{t('cm.orders')}{sortIcon('auftraege')}</th>
              <th className="px-3 py-2 text-[10px] font-semibold uppercase text-[var(--text-tertiary)] text-right cursor-pointer select-none" onClick={() => toggleSort('komplexitaet_index')}>{t('cm.komplexitaetShort')}{sortIcon('komplexitaet_index')}</th>
              <th className="px-3 py-2 text-[10px] font-semibold uppercase text-[var(--text-tertiary)] text-right cursor-pointer select-none" onClick={() => toggleSort('effizienz_index')}>{t('cm.effizienzShort')}{sortIcon('effizienz_index')}</th>
              <th className="px-3 py-2 text-[10px] font-semibold uppercase text-[var(--text-tertiary)] text-right cursor-pointer select-none" onClick={() => toggleSort('db_gesamt')}>DB{sortIcon('db_gesamt')}</th>
              <th className="px-3 py-2 text-[10px] font-semibold uppercase text-[var(--text-tertiary)] text-right cursor-pointer select-none" onClick={() => toggleSort('db_pro_stunde')}>DB/h{sortIcon('db_pro_stunde')}</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map(k => {
              const eBadge = effizienzBadge(k.effizienz_index || 0);
              const barW = k.db_pro_stunde != null ? Math.min(100, Math.max(0, (k.db_pro_stunde / maxDbH) * 100)) : 0;
              return (
                <tr key={k.kunde_id} className="border-b border-line-subtle hover:bg-surface-hover transition-colors">
                  <td className="px-3 py-2 text-left">
                    <Link to={`/kunden/${k.kunde_id}`} className="text-xs font-medium text-content hover:text-primary-500 transition-colors">
                      {k.name}
                    </Link>
                    {k.segment && <span className={`ml-1.5 text-[9px] font-bold px-1.5 py-0.5 rounded ${k.segment === 'A' ? 'bg-primary-500/15 text-primary-400' : k.segment === 'B' ? 'bg-purple-500/15 text-purple-400' : 'bg-amber-500/15 text-amber-400'}`}>{k.segment}</span>}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-xs text-content">{k.auftraege.toLocaleString(locale)}</td>
                  <td className="px-3 py-2 text-right">
                    <span className="font-mono text-xs text-content">{k.komplexitaet_index.toFixed(1).replace('.', ',')} min</span>
                    <span className="ml-1 text-[9px] text-[var(--text-tertiary)]">{komplexitaetLabel(k.komplexitaet_index, t)}</span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    {k.effizienz_index != null ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold"
                        style={{ backgroundColor: eBadge.bg, color: eBadge.text }}>
                        <span className="dark:hidden">{k.effizienz_index.toFixed(0)}% {eBadge.arrow}</span>
                        <span className="hidden dark:inline" style={{ color: eBadge.darkText }}>{k.effizienz_index.toFixed(0)}% {eBadge.arrow}</span>
                      </span>
                    ) : <span className="text-xs text-[var(--text-tertiary)]">–</span>}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-xs text-content">{fmtEur(k.db_gesamt)}</td>
                  <td className="px-3 py-2 text-right relative overflow-hidden">
                    <div className="absolute inset-y-0 left-0 opacity-15 transition-all"
                      style={{ width: `${barW}%`, backgroundColor: dbProStundeColor(k.db_pro_stunde || 0) }} />
                    <span className="relative font-mono text-xs font-semibold" style={{ color: dbProStundeColor(k.db_pro_stunde || 0) }}>
                      {k.db_pro_stunde != null ? `${k.db_pro_stunde.toFixed(1).replace('.', ',')} €` : '–'}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
